import type {
  EduSessionSeries,
  EduSession,
  EduYearHoliday,
  EduSessionStatus,
} from '../../types/schema'
import {
  expandOccurrences,
  sessionKey,
  type EduHoliday,
} from '../../utils/eduSessionPattern'

export interface TimetableOccurrence {
  id: string
  seriesId: string
  date: string
  startTime: string
  durationMin: number
  nodeId: string
  classId: string
  teacherUids: string[]
  roomId?: string | null
  onlineUrl?: string | null
  status: EduSessionStatus
  substituteUid?: string | null
  topicItemIds?: string[]
  note?: string | null
}

export interface WeekDayCol {
  date: string
  weekday: number // 1 = Mon ... 6 = Sat, 0 = Sun
  key: string
}

function parseUtcDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function formatUtcDate(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function addDaysUtc(dateStr: string, days: number): string {
  const dt = parseUtcDate(dateStr)
  dt.setUTCDate(dt.getUTCDate() + days)
  return formatUtcDate(dt)
}

export function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number)
  return h * 60 + m
}

export interface SlotTimeWindow {
  date: string
  startTime: string
  durationMin: number
}

export interface TeacherCandidate {
  uid: string
  displayName?: string
  name?: string
  role?: string
}

export interface TeacherLeaveRecord {
  teacherUid: string
  fromDate: string
  toDate: string
}

export function areTimeIntervalsOverlapping(
  startA: string,
  durationA: number,
  startB: string,
  durationB: number,
): boolean {
  const aStart = timeToMinutes(startA)
  const aEnd = aStart + durationA
  const bStart = timeToMinutes(startB)
  const bEnd = bStart + durationB
  return aStart < bEnd && bStart < aEnd
}

export function isTeacherFreeForSlot(
  teacherUid: string,
  slot: SlotTimeWindow,
  occurrences: TimetableOccurrence[],
  approvedLeaves?: TeacherLeaveRecord[],
): boolean {
  if (approvedLeaves && approvedLeaves.length > 0) {
    const onLeave = approvedLeaves.some(
      (l) => l.teacherUid === teacherUid && l.fromDate <= slot.date && slot.date <= l.toDate,
    )
    if (onLeave) return false
  }

  const conflicting = occurrences.some((occ) => {
    if (occ.date !== slot.date) return false
    if (occ.status === 'CANCELLED') return false
    const isAssigned =
      (occ.teacherUids && occ.teacherUids.includes(teacherUid)) ||
      occ.substituteUid === teacherUid
    if (!isAssigned) return false

    return areTimeIntervalsOverlapping(
      slot.startTime,
      slot.durationMin,
      occ.startTime,
      occ.durationMin,
    )
  })

  return !conflicting
}

export function findFreeTeachersForSlot<T extends TeacherCandidate>(
  slot: SlotTimeWindow & { excludeTeacherUids?: string[] },
  teachers: T[],
  occurrences: TimetableOccurrence[],
  approvedLeaves?: TeacherLeaveRecord[],
): T[] {
  const excluded = new Set(slot.excludeTeacherUids || [])
  return teachers.filter((teacher) => {
    if (excluded.has(teacher.uid)) return false
    return isTeacherFreeForSlot(teacher.uid, slot, occurrences, approvedLeaves)
  })
}

export function isSessionGap(
  occurrence: TimetableOccurrence,
  approvedLeaves?: TeacherLeaveRecord[],
): boolean {
  if (occurrence.status === 'CANCELLED') return false
  if (occurrence.substituteUid) return false

  const teachers = occurrence.teacherUids || []
  if (teachers.length === 0) return true

  if (approvedLeaves && approvedLeaves.length > 0) {
    const allOnLeave = teachers.every((uid) =>
      approvedLeaves.some(
        (l) => l.teacherUid === uid && l.fromDate <= occurrence.date && occurrence.date <= l.toDate,
      ),
    )
    if (allOnLeave) return true
  }

  return false
}

/**
 * Returns Monday (ISO day 1) of the ISO week containing dateStr.
 */
export function getStartOfIsoWeek(dateStr: string): string {
  const dt = parseUtcDate(dateStr)
  const day = dt.getUTCDay() // 0 = Sun, 1 = Mon ... 6 = Sat
  const diffToMon = day === 0 ? -6 : 1 - day
  dt.setUTCDate(dt.getUTCDate() + diffToMon)
  return formatUtcDate(dt)
}

/**
 * Computes the 7 consecutive ISO date strings for a week starting on Monday.
 */
export function computeWeekDates(mondayIsoDate: string): string[] {
  const dates: string[] = []
  for (let i = 0; i < 7; i++) {
    dates.push(addDaysUtc(mondayIsoDate, i))
  }
  return dates
}

/**
 * Clamps an anchor date to optional minDate and maxDate.
 */
export function clampDateToRange(
  dateStr: string,
  minDate?: string | null,
  maxDate?: string | null,
): string {
  if (minDate && dateStr < minDate) {
    return minDate
  }
  if (maxDate && dateStr > maxDate) {
    return maxDate
  }
  return dateStr
}

/**
 * The weekend column rule (EQ47):
 * Mon–Fri always; Saturday / Sunday appear only when a visible session falls on them.
 */
export function getVisibleWeekDays(
  weekDates: string[],
  sessions: TimetableOccurrence[],
): WeekDayCol[] {
  if (weekDates.length < 5) return []

  const satDate = weekDates[5]
  const sunDate = weekDates[6]

  const hasSat = sessions.some((s) => s.date === satDate)
  const hasSun = sessions.some((s) => s.date === sunDate)

  const cols: WeekDayCol[] = [
    { date: weekDates[0], weekday: 1, key: 'MON' },
    { date: weekDates[1], weekday: 2, key: 'TUE' },
    { date: weekDates[2], weekday: 3, key: 'WED' },
    { date: weekDates[3], weekday: 4, key: 'THU' },
    { date: weekDates[4], weekday: 5, key: 'FRI' },
  ]

  if (hasSat && satDate) {
    cols.push({ date: satDate, weekday: 6, key: 'SAT' })
  }
  if (hasSun && sunDate) {
    cols.push({ date: sunDate, weekday: 0, key: 'SUN' })
  }

  return cols
}

export interface SlotBlockPlacement {
  topPercent: number
  heightPercent: number
  startMinutes: number
  endMinutes: number
}

export interface GridHours {
  startHour: number
  endHour: number
}

/**
 * Computes grid start and end hours to encompass all given occurrences,
 * with a default minimum window of 08:00 to 18:00, capped to [0, 24].
 */
export function computeGridHours(
  occurrences: Array<{ startTime: string; durationMin: number }>,
): GridHours {
  let minStartHour = 8
  let maxEndHour = 18

  for (const occ of occurrences) {
    const startMinutes = timeToMinutes(occ.startTime)
    const endMinutes = startMinutes + occ.durationMin

    const occStartHour = Math.floor(startMinutes / 60)
    const occEndHour = Math.ceil(endMinutes / 60)

    if (occStartHour < minStartHour) {
      minStartHour = occStartHour
    }
    if (occEndHour > maxEndHour) {
      maxEndHour = occEndHour
    }
  }

  const startHour = Math.max(0, Math.min(minStartHour, 24))
  const endHour = Math.max(0, Math.min(maxEndHour, 24))

  return {
    startHour,
    endHour,
  }
}

/**
 * Computes block placement percentages relative to the daily grid window.
 * Default grid window: 08:00 (480m) to 18:00 (1080m) = 600m total.
 */
export function computeSlotBlockPlacement(
  startTime: string,
  durationMin: number,
  gridStartHour = 8,
  gridEndHour = 18,
): SlotBlockPlacement {
  const gridStartMinutes = gridStartHour * 60
  const gridTotalMinutes = (gridEndHour - gridStartHour) * 60

  const startMinutes = timeToMinutes(startTime)
  const endMinutes = startMinutes + durationMin

  const offsetMinutes = Math.max(0, startMinutes - gridStartMinutes)
  const topPercent = (offsetMinutes / gridTotalMinutes) * 100
  const heightPercent = (durationMin / gridTotalMinutes) * 100

  return {
    topPercent,
    heightPercent,
    startMinutes,
    endMinutes,
  }
}

/**
 * Checks if a given date falls within any holiday range.
 */
export function getHolidayForDate(
  dateStr: string,
  holidays: EduYearHoliday[] = [],
): EduYearHoliday | null {
  for (const h of holidays) {
    if (h.from <= dateStr && dateStr <= h.to) {
      return h
    }
  }
  return null
}

/**
 * Expands series into occurrences within the window and merges any saved session documents
 * (e.g. CANCELLED status, substitutes, topics).
 */
export function mergeSessionOccurrences(
  seriesList: EduSessionSeries[],
  savedDocs: EduSession[],
  holidays: EduYearHoliday[] = [],
  windowFrom: string,
  windowTo: string,
): TimetableOccurrence[] {
  const savedDocMap = new Map<string, EduSession>()
  for (const doc of savedDocs) {
    const key = doc.seriesId ? sessionKey(doc.seriesId, doc.date) : doc.id
    if (key) {
      savedDocMap.set(key, doc)
    }
  }

  const occurrences: TimetableOccurrence[] = []
  const eduHolidays: EduHoliday[] = holidays.map((h) => ({
    from: h.from,
    to: h.to,
    label: h.label,
  }))

  for (const series of seriesList) {
    if (!series.id) continue

    const patternDates = expandOccurrences(
      {
        weekdays: series.weekdays,
        startTime: series.startTime,
        durationMin: series.durationMin,
        fromDate: series.fromDate,
        toDate: series.toDate,
      },
      eduHolidays,
      windowFrom,
      windowTo,
    )

    for (const date of patternDates) {
      const key = sessionKey(series.id, date)
      const saved = savedDocMap.get(key)

      const status: EduSessionStatus = saved?.status ?? 'PLANNED'
      const substituteUid = saved?.substituteUid ?? null
      const topicItemIds = saved?.topicItemIds ?? []
      const note = saved?.note ?? null

      occurrences.push({
        id: key,
        seriesId: series.id,
        date,
        startTime: series.startTime,
        durationMin: series.durationMin,
        nodeId: series.nodeId,
        classId: series.classId,
        teacherUids: series.teacherUids || [],
        roomId: series.roomId,
        onlineUrl: series.onlineUrl,
        status,
        substituteUid,
        topicItemIds,
        note,
      })
    }
  }

  return occurrences
}
